/**
 * v3.config — /v3/config/* (22 PLANNED GET routes, all 503 until v3.0 ETL seeds SQL).
 * All cached 5 min server-side.
 *
 * Route ambiguity note (api-wiring §6.2): WIRING-GUIDE calls out 22 one-per-SP-list
 * SQL tables; sql-target-schema.md expands to 42 decomposed tables. Both agree
 * on the 22 API routes. Each route here maps to exactly one `/v3/config/*` path.
 */

import type {
  RequestOptions,
  ApiResult,
  Product,
  Feature,
  ProductFeature,
  CardType,
  DisclosureTemplate,
  EntityRule,
  EntityRestriction,
  UserRoleDto,
  Region,
  Branch,
  NaicsCode,
  CdPenaltyRule,
  CdPenaltyWaiverReason,
  SdbTimelineMilestone,
  IraConfigRow,
  IraReasonCode,
  IraTransactionCode,
  IraInstitutionRelationship,
  IraContributionPhaseout,
  AlertThreshold,
  PipelineDefinition,
  SlaTarget,
  SignatureAuthRule,
  RetentionPolicy,
  SystemSetting,
  CipIdPriorityRow,
  NotificationTemplatesEnvelope,
  DocumentRequirementsEnvelope,
  StateGovSearchLinksEnvelope,
  StateGovSearchType,
  SystemErrorCode,
  PurgeReasonCode,
  AlienConfigEnvelope,
  CoreDirectorCode,
  LnVerificationRule,
  PepConfig,
} from '@retail-ops/shared-types';
import type { ApiHttpClient } from '../http.js';

// Keyed sub-API for routes with query params.
export class ConfigUsersApi {
  public constructor(private readonly http: ApiHttpClient) {}

  public byEmail(email: string, opts?: RequestOptions): Promise<ApiResult<UserRoleDto>> {
    return this.http.get<ApiResult<UserRoleDto>>('/v3/config/user-roles', {
      ...opts,
      query: { email },
    });
  }

  /** Full roster (not yet exposed; reserved for admin screens). */
  public list(opts?: RequestOptions): Promise<ApiResult<ReadonlyArray<UserRoleDto>>> {
    return this.http.get<ApiResult<ReadonlyArray<UserRoleDto>>>('/v3/config/user-roles', {
      ...opts,
    });
  }
}

export class ConfigNaicsApi {
  public constructor(private readonly http: ApiHttpClient) {}

  public byPrefix(
    prefix: string,
    opts?: RequestOptions
  ): Promise<ApiResult<NaicsConfigEnvelope>> {
    return this.http.get<ApiResult<NaicsConfigEnvelope>>('/v3/config/naics', {
      ...opts,
      query: { prefix },
    });
  }

  public all(opts?: RequestOptions): Promise<ApiResult<NaicsConfigEnvelope>> {
    return this.http.get<ApiResult<NaicsConfigEnvelope>>('/v3/config/naics', {
      ...opts,
    });
  }
}

/**
 * v0.1.36 people-form port — DocumentRequirements catalog.
 * Route: GET /v3/config/document-requirements[?entityType=Individual]
 * Returns `{ version, requirements }` envelope. See port-design §10/§12.
 */
export class ConfigDocumentRequirementsApi {
  public constructor(private readonly http: ApiHttpClient) {}

  /**
   * List document-requirement rows, optionally filtered by entity type.
   * Pass `entityType` (e.g. 'Individual', 'LLC', 'RevocableTrust') to
   * narrow to a single type; omit to retrieve the full catalog.
   */
  public list(
    entityType?: string,
    opts?: RequestOptions
  ): Promise<ApiResult<DocumentRequirementsEnvelope>> {
    const query = entityType ? { entityType } : undefined;
    return this.http.get<ApiResult<DocumentRequirementsEnvelope>>(
      '/v3/config/document-requirements',
      { ...opts, ...(query ? { query } : {}) }
    );
  }
}

/**
 * v0.1.36 people-form port — StateGovSearchLinks catalog.
 * Route: GET /v3/config/state-gov-search-links[?stateCode=MO&searchType=Business]
 * Returns `{ version, links }` envelope. See port-design §11.
 */
export class ConfigStateGovSearchLinksApi {
  public constructor(private readonly http: ApiHttpClient) {}

  /**
   * List state-gov search links, optionally filtered by state and/or type.
   * Both filters are optional and can be combined.
   */
  public list(
    stateCode?: string,
    searchType?: StateGovSearchType,
    opts?: RequestOptions
  ): Promise<ApiResult<StateGovSearchLinksEnvelope>> {
    const query: Record<string, string> = {};
    if (stateCode) query.stateCode = stateCode;
    if (searchType) query.searchType = searchType;
    return this.http.get<ApiResult<StateGovSearchLinksEnvelope>>(
      '/v3/config/state-gov-search-links',
      { ...opts, ...(Object.keys(query).length > 0 ? { query } : {}) }
    );
  }
}

/**
 * BACKLOG-0762 — SEVIS school catalog row, GET /v3/config/schools/* (alpha.171).
 * Wire shape per ConfigSchoolsFunction.Shape (camelCase `{success,data}`
 * envelope; see docs/operations/sevis-school-catalog-runbook.md §3).
 *
 * `f141Employer` is the SERVER-computed Core Director **F141 Employer**
 * value (F141 = 15 chars, Bill 2026-06-12): `commonName15 ?? smart
 * word-boundary truncation of campusName`. It is the single source of truth
 * for the F141 value — clients must NOT re-derive it.
 */
export interface SevisSchoolRow {
  /** SEVIS campus code, e.g. 'KAN214F10190000'. */
  campusCode: string;
  /** Parent school name (secondary display text). */
  schoolName: string;
  /** Campus-level name — the display truth for the Work/School field. */
  campusName: string;
  /** SEVP-certified to enroll F (academic) students. */
  fVisa: boolean;
  /** SEVP-certified to enroll M (vocational) students. */
  mVisa: boolean;
  city: string;
  state: string;
  /** Curated ≤15-char short name (Bill's mapping); null when uncurated. */
  commonName15: string | null;
  /** 1 = market-area pin, 2 = rest of MO, null = out-of-state. */
  marketRank: number | null;
  /** DHS certified-school list date this row was last seen on (ISO). */
  sourceListDate: string;
  /** False = decertified (kept for warn-not-dead-end lookups). */
  isActive: boolean;
  /** CD F141 Employer value (≤15 chars, server-computed — do not re-derive). */
  f141Employer: string;
}

/**
 * BACKLOG-0762 — SEVIS school catalog lookups (shipped alpha.171).
 * Routes per docs/operations/sevis-school-catalog-runbook.md §3.
 */
export class ConfigSchoolsApi {
  public constructor(private readonly http: ApiHttpClient) {}

  /**
   * GET /v3/config/schools/{campusCode} — exact SEVIS-code lookup.
   * 404 (`SCHOOL_NOT_FOUND`) when the code isn't in the catalog — surfaced
   * as a thrown ApiError; callers on the capture path treat it as non-fatal.
   * Decertified rows ARE returned (`isActive:false`) so the client can warn
   * instead of dead-ending.
   */
  public byCode(
    campusCode: string,
    opts?: RequestOptions
  ): Promise<ApiResult<SevisSchoolRow>> {
    return this.http.get<ApiResult<SevisSchoolRow>>(
      `/v3/config/schools/${encodeURIComponent(campusCode.trim().toUpperCase())}`,
      { ...opts }
    );
  }

  /**
   * GET /v3/config/schools?q= — FSR manual-pick name search (active rows
   * only, top 20, MarketRank tier ascending then SchoolName). Empty/absent
   * `q` returns the curated regional pins — use as the picker's default
   * suggestion set.
   */
  public search(
    q?: string,
    opts?: RequestOptions
  ): Promise<ApiResult<ReadonlyArray<SevisSchoolRow>>> {
    const trimmed = (q ?? '').trim();
    return this.http.get<ApiResult<ReadonlyArray<SevisSchoolRow>>>(
      '/v3/config/schools',
      { ...opts, ...(trimmed.length > 0 ? { query: { q: trimmed } } : {}) }
    );
  }
}

/**
 * Composite-envelope wire shapes for the five /v3/config routes that return an
 * OBJECT of sibling arrays — NOT a bare array. These methods were previously
 * typed `ApiResult<ReadonlyArray<Row>>`, which is wrong: a caller doing
 * `.filter`/`.map`/`.length` on the result silently got `undefined` or threw
 * ("t.filter is not a function"). Each handler returns the envelope below
 * (verified against V3ConfigFunction.cs 2026-06-23). Read the primary list off
 * the named key. The non-primary sibling arrays are loosely typed
 * (`Record<string,unknown>`) until they earn a shared-type.
 */
export interface NaicsConfigEnvelope {
  /** NAICS code rows — the primary list. */
  codes: ReadonlyArray<NaicsCode>;
  sectors: ReadonlyArray<Record<string, unknown>>;
}

export interface IraConfigEnvelope {
  /** IRA plan-prefix rows — the primary list. */
  planPrefixes: ReadonlyArray<IraConfigRow>;
  rateIndexes: ReadonlyArray<Record<string, unknown>>;
  rmdFactors: ReadonlyArray<Record<string, unknown>>;
  contributionLimits: ReadonlyArray<Record<string, unknown>>;
}

export interface AlertThresholdsEnvelope {
  /** Threshold rows — the primary list. */
  thresholds: ReadonlyArray<AlertThreshold>;
  categories: ReadonlyArray<Record<string, unknown>>;
}

export interface CdPenaltyRulesEnvelope {
  /** CD early-withdrawal penalty rules — the primary list. */
  rules: ReadonlyArray<CdPenaltyRule>;
  /** Waiver-reason rows (also served bare at /v3/config/cd-penalty-waiver-reasons). */
  waiverReasons: ReadonlyArray<CdPenaltyWaiverReason>;
}

export interface SlaTargetsEnvelope {
  /** SLA target rows — the primary list. */
  targets: ReadonlyArray<SlaTarget>;
  stages: ReadonlyArray<Record<string, unknown>>;
  origins: ReadonlyArray<Record<string, unknown>>;
}

export interface StaffDirectoryEntry {
  name: string | null;
  title: string | null;
  department: string | null;
  photo: string | null;
  /** Teams-card details (pcb-retail-api 3.23.11+); absent from older APIs. */
  mail?: string | null;
  mobile?: string | null;
  workPhone?: string | null;
  office?: string | null;
  manager?: { upn: string | null; name: string | null; title: string | null } | null;
}

export class ConfigApi {
  public readonly users: ConfigUsersApi;
  public readonly naics: ConfigNaicsApi;
  public readonly documentRequirements: ConfigDocumentRequirementsApi;
  public readonly stateGovSearchLinks: ConfigStateGovSearchLinksApi;
  /** BACKLOG-0762 — SEVIS school catalog lookups (I-20 capture wiring). */
  public readonly schools: ConfigSchoolsApi;

  public constructor(private readonly http: ApiHttpClient) {
    this.users = new ConfigUsersApi(http);
    this.naics = new ConfigNaicsApi(http);
    this.documentRequirements = new ConfigDocumentRequirementsApi(http);
    this.stateGovSearchLinks = new ConfigStateGovSearchLinksApi(http);
    this.schools = new ConfigSchoolsApi(http);
  }

  // ---------------------------------------------------------------------------
  // 22 GET endpoints
  // ---------------------------------------------------------------------------

  /** Entra photos of active officers as data URIs, keyed by Core operator code (absent = no photo). */
  public officerPhotos(opts?: RequestOptions): Promise<ApiResult<Readonly<Record<string, string>>>> {
    return this.http.get<ApiResult<Readonly<Record<string, string>>>>('/v3/config/officer-photos', { ...opts });
  }

  /** Live Entra profile + photo per staff UPN (max 50): name, title, department, photo (data URI or null). */
  public staffDirectory(upns: ReadonlyArray<string>, opts?: RequestOptions): Promise<ApiResult<Readonly<Record<string, StaffDirectoryEntry>>>> {
    return this.http.get<ApiResult<Readonly<Record<string, StaffDirectoryEntry>>>>(
      `/v3/config/staff-directory?upns=${encodeURIComponent(upns.join(','))}`, { ...opts });
  }

  /**
   * Send a Teams 1:1 message AS the signed-in officer (POST /v3/staff/chat-message, OBO). The
   * reference adds a "Re: <title>" line linking into the Retail app (cart id). Rejects / returns
   * success:false with errorCode CONSENT_REQUIRED or NOT_CONFIGURED until chat sending is
   * switched on - callers fall back to opening the chat with the text filled in.
   */
  public staffChatMessage(
    body: { toUpn: string; text: string; reference?: { title: string; cartId?: string | null } | null },
    opts?: RequestOptions,
  ): Promise<{ success: boolean; errorCode?: string; error?: string }> {
    return this.http.post<{ success: boolean; errorCode?: string; error?: string }>(
      '/v3/staff/chat-message', { ...opts, body });
  }

  public products(opts?: RequestOptions): Promise<ApiResult<ReadonlyArray<Product>>> {
    return this.http.get<ApiResult<ReadonlyArray<Product>>>('/v3/config/products', { ...opts });
  }

  public features(opts?: RequestOptions): Promise<ApiResult<ReadonlyArray<Feature>>> {
    return this.http.get<ApiResult<ReadonlyArray<Feature>>>('/v3/config/features', { ...opts });
  }

  public productFeatures(
    opts?: RequestOptions
  ): Promise<ApiResult<ReadonlyArray<ProductFeature>>> {
    return this.http.get<ApiResult<ReadonlyArray<ProductFeature>>>(
      '/v3/config/product-features',
      { ...opts }
    );
  }

  public cardTypes(opts?: RequestOptions): Promise<ApiResult<ReadonlyArray<CardType>>> {
    return this.http.get<ApiResult<ReadonlyArray<CardType>>>('/v3/config/card-types', { ...opts });
  }

  public disclosures(
    opts?: RequestOptions
  ): Promise<ApiResult<ReadonlyArray<DisclosureTemplate>>> {
    return this.http.get<ApiResult<ReadonlyArray<DisclosureTemplate>>>(
      '/v3/config/disclosures',
      { ...opts }
    );
  }

  public entityRules(opts?: RequestOptions): Promise<ApiResult<ReadonlyArray<EntityRule>>> {
    return this.http.get<ApiResult<ReadonlyArray<EntityRule>>>('/v3/config/entity-rules', { ...opts });
  }

  public entityRestrictions(
    opts?: RequestOptions
  ): Promise<ApiResult<ReadonlyArray<EntityRestriction>>> {
    return this.http.get<ApiResult<ReadonlyArray<EntityRestriction>>>(
      '/v3/config/entity-restrictions',
      { ...opts }
    );
  }

  /** Convenience: default to `users.list()` shape; prefer `users.byEmail(x)`. */
  public userRoles(
    filters?: { email?: string },
    opts?: RequestOptions
  ): Promise<ApiResult<UserRoleDto> | ApiResult<ReadonlyArray<UserRoleDto>>> {
    if (filters?.email) return this.users.byEmail(filters.email, opts);
    return this.users.list(opts);
  }

  public regions(opts?: RequestOptions): Promise<ApiResult<ReadonlyArray<Region>>> {
    return this.http.get<ApiResult<ReadonlyArray<Region>>>('/v3/config/regions', { ...opts });
  }

  public branches(opts?: RequestOptions): Promise<ApiResult<ReadonlyArray<Branch>>> {
    return this.http.get<ApiResult<ReadonlyArray<Branch>>>('/v3/config/branches', { ...opts });
  }

  public naicsCodes(
    filters?: { codePrefix?: string },
    opts?: RequestOptions
  ): Promise<ApiResult<NaicsConfigEnvelope>> {
    if (filters?.codePrefix) return this.naics.byPrefix(filters.codePrefix, opts);
    return this.naics.all(opts);
  }

  public systemSettings(
    opts?: RequestOptions
  ): Promise<ApiResult<Record<string, unknown>>> {
    return this.http.get<ApiResult<Record<string, unknown>>>('/v3/config/system-settings', {
      ...opts,
    });
  }

  /**
   * Politically Exposed Person (PEP) control config (Bill 2026-06-17).
   * GET /v3/config/pep — assembles the `pep.*` SystemSettings keys into a
   * defaulted PepConfig. Read by the FSR Verify-section checkbox + helper.
   */
  public pep(opts?: RequestOptions): Promise<ApiResult<PepConfig>> {
    return this.http.get<ApiResult<PepConfig>>('/v3/config/pep', { ...opts });
  }

  /**
   * CIP ID priority — the F254-ascending primary/secondary slot ranking that
   * drives assignCipSlots. GET /v3/config/cip-id-priority. Global ranking (no
   * cohort dimension): lower rank fills CIP ID1, next-best fills ID2.
   */
  public cipIdPriority(
    opts?: RequestOptions
  ): Promise<ApiResult<ReadonlyArray<CipIdPriorityRow>>> {
    return this.http.get<ApiResult<ReadonlyArray<CipIdPriorityRow>>>(
      '/v3/config/cip-id-priority',
      { ...opts }
    );
  }

  public systemSettingsRows(
    opts?: RequestOptions
  ): Promise<ApiResult<ReadonlyArray<SystemSetting>>> {
    return this.http.get<ApiResult<ReadonlyArray<SystemSetting>>>(
      '/v3/config/system-settings/rows',
      { ...opts }
    );
  }

  public cdPenaltyRules(
    opts?: RequestOptions
  ): Promise<ApiResult<CdPenaltyRulesEnvelope>> {
    return this.http.get<ApiResult<CdPenaltyRulesEnvelope>>(
      '/v3/config/cd-penalty-rules',
      { ...opts }
    );
  }

  /**
   * GET /v3/config/cd-penalty-waiver-reasons — 4 rows per
   * scripts/seed/08-cd-penalty-rules.sql + sql-target-schema §5.7.6.
   * Until the Function route lands (Wave 3), this 503s and ConfigContext
   * leaves `cdPenaltyWaiverReasons` empty; PenaltyWaiverWizard falls back
   * to its seed-matching FALLBACK_REASONS only in that case.
   */
  public cdPenaltyWaiverReasons(
    opts?: RequestOptions
  ): Promise<ApiResult<ReadonlyArray<CdPenaltyWaiverReason>>> {
    return this.http.get<ApiResult<ReadonlyArray<CdPenaltyWaiverReason>>>(
      '/v3/config/cd-penalty-waiver-reasons',
      { ...opts }
    );
  }

  public sdbTimeline(
    opts?: RequestOptions
  ): Promise<ApiResult<ReadonlyArray<SdbTimelineMilestone>>> {
    return this.http.get<ApiResult<ReadonlyArray<SdbTimelineMilestone>>>(
      '/v3/config/sdb-timeline',
      { ...opts }
    );
  }

  // `complianceQuestions()` retired 2026-05-08 — ComplianceQuestion kill plan
  // (route returns 404; compliance gating runs inline per-stage now).

  public iraConfig(opts?: RequestOptions): Promise<ApiResult<IraConfigEnvelope>> {
    return this.http.get<ApiResult<IraConfigEnvelope>>(
      '/v3/config/ira-config',
      { ...opts }
    );
  }

  /**
   * GET /v3/config/ira-reason-codes — IRA distribution-reason catalog
   * (Wave 5.1 / Agent IRA-FN-REASON-CODES). Active rows only, ordered by
   * SortOrder. Replaces the FALLBACK_REASONS hardcode in
   * apps/ROP-teams/src/modules/RIM/flows/DistributionFlow.tsx.
   * Backed by dbo.IraReasonCodes — see seed 09-ira-reason-codes.sql.
   */
  public iraReasonCodes(
    opts?: RequestOptions
  ): Promise<ApiResult<ReadonlyArray<IraReasonCode>>> {
    return this.http.get<ApiResult<ReadonlyArray<IraReasonCode>>>(
      '/v3/config/ira-reason-codes',
      { ...opts }
    );
  }

  /**
   * GET /v3/config/ira-transaction-codes — Q4/Q5 admin-config'd TC sequences.
   * Per Bill's 2026-04-25 sign-off, In-House-Transfer + SIMPLE-contribution
   * TC chains are NOT hardcoded — Emily / IRA team fills via the admin
   * editor post-deploy. Returns active rows only, ordered by (Scenario,
   * SequenceIndex). Backed by dbo.IraTransactionCodes — seed 10-ira-
   * transaction-codes.sql ships intentionally EMPTY at v3.0 launch.
   */
  public iraTransactionCodes(
    opts?: RequestOptions
  ): Promise<ApiResult<ReadonlyArray<IraTransactionCode>>> {
    return this.http.get<ApiResult<ReadonlyArray<IraTransactionCode>>>(
      '/v3/config/ira-transaction-codes',
      { ...opts }
    );
  }

  /**
   * GET /v3/config/ira-institution-relationships — Q3 carve-out catalog.
   * Per Bill's 2026-04-25 sign-off, the Raymond James / Verna early-
   * withdrawal penalty exemption lives here as one row (PenaltyExempt=true)
   * instead of a hardcoded branch. Backed by dbo.IraInstitutionRelationships.
   */
  public iraInstitutionRelationships(
    opts?: RequestOptions
  ): Promise<ApiResult<ReadonlyArray<IraInstitutionRelationship>>> {
    return this.http.get<ApiResult<ReadonlyArray<IraInstitutionRelationship>>>(
      '/v3/config/ira-institution-relationships',
      { ...opts }
    );
  }

  /**
   * GET /v3/config/ira-contribution-phaseouts — port-plan §2.2.6 net-new.
   * MAGI phaseout brackets per (taxYear, planCode, filingStatus). Drives
   * Trad-IRA deductibility caps + Roth-IRA eligibility caps. Seeded with
   * 2025 + 2026 brackets per IRS Pub 590-A; admin updates annually.
   * Backed by dbo.IraContributionPhaseouts — seed 12-ira-contribution-phaseouts.sql.
   */
  public iraContributionPhaseouts(
    opts?: RequestOptions
  ): Promise<ApiResult<ReadonlyArray<IraContributionPhaseout>>> {
    return this.http.get<ApiResult<ReadonlyArray<IraContributionPhaseout>>>(
      '/v3/config/ira-contribution-phaseouts',
      { ...opts }
    );
  }

  public alertThresholds(
    opts?: RequestOptions
  ): Promise<ApiResult<AlertThresholdsEnvelope>> {
    return this.http.get<ApiResult<AlertThresholdsEnvelope>>(
      '/v3/config/alert-thresholds',
      { ...opts }
    );
  }

  public pipelineDefinitions(
    opts?: RequestOptions
  ): Promise<ApiResult<ReadonlyArray<PipelineDefinition>>> {
    return this.http.get<ApiResult<ReadonlyArray<PipelineDefinition>>>(
      '/v3/config/pipeline-definitions',
      { ...opts }
    );
  }

  public slaTargets(opts?: RequestOptions): Promise<ApiResult<SlaTargetsEnvelope>> {
    return this.http.get<ApiResult<SlaTargetsEnvelope>>(
      '/v3/config/sla-targets',
      { ...opts }
    );
  }

  public signatureAuthRules(
    opts?: RequestOptions
  ): Promise<ApiResult<ReadonlyArray<SignatureAuthRule>>> {
    return this.http.get<ApiResult<ReadonlyArray<SignatureAuthRule>>>(
      '/v3/config/signature-auth-rules',
      { ...opts }
    );
  }

  public retentionPolicies(
    opts?: RequestOptions
  ): Promise<ApiResult<ReadonlyArray<RetentionPolicy>>> {
    return this.http.get<ApiResult<ReadonlyArray<RetentionPolicy>>>(
      '/v3/config/retention-policies',
      { ...opts }
    );
  }

  /**
   * Route 21 — NOT a bare array. The Function returns a composite envelope
   * `{templates, versions, variables, routingRules, channels, categories}`.
   * See ConfigFunction.cs §21 (`NotificationTemplates`) and shared-types'
   * `NotificationTemplatesEnvelope`. Callers that only want the template rows
   * read `.templates` off the envelope.
   */
  public notificationTemplates(
    opts?: RequestOptions
  ): Promise<ApiResult<NotificationTemplatesEnvelope>> {
    return this.http.get<ApiResult<NotificationTemplatesEnvelope>>(
      '/v3/config/notification-templates',
      { ...opts }
    );
  }

  // ---------------------------------------------------------------------------
  // Wave 6.3 — Records management closed catalogs
  // ---------------------------------------------------------------------------

  /**
   * GET /v3/config/system-error-codes — closed catalog for the System-Error
   * resolution-code path. Active rows only, ordered by SortOrder. Backed by
   * dbo.SystemErrorCodes (Wave 6.3 backend agent).
   */
  public systemErrorCodes(
    opts?: RequestOptions,
  ): Promise<ApiResult<ReadonlyArray<SystemErrorCode>>> {
    return this.http.get<ApiResult<ReadonlyArray<SystemErrorCode>>>(
      '/v3/config/system-error-codes',
      { ...opts },
    );
  }

  /**
   * GET /v3/config/purge-reason-codes — closed catalog used by the
   * SuperAdmin records management hard-delete dialog. Backed by
   * dbo.PurgeReasonCodes.
   */
  public purgeReasonCodes(
    opts?: RequestOptions,
  ): Promise<ApiResult<ReadonlyArray<PurgeReasonCode>>> {
    return this.http.get<ApiResult<ReadonlyArray<PurgeReasonCode>>>(
      '/v3/config/purge-reason-codes',
      { ...opts },
    );
  }

  // ---------------------------------------------------------------------------
  // Tier-1 paired reads (Bill 2026-05-14)
  // ---------------------------------------------------------------------------
  // The 13 catalogs below previously only exposed an admin-namespaced
  // read at `/v3/admin/<resource>`, blocking operational SWAs (retail /
  // sysops / risk / portal) from reading config without admin-tier auth.
  // Each method here pairs the existing admin GET with a config-namespace
  // read that returns the same wire shape. Writes (Upsert / Delete) stay
  // on the admin namespace. See AUDIT.md §3.5 +
  // project_admin_swa_topology_lock_20260514.md.
  //
  // Row shape: `Record<string, unknown>` — consumers cast to the local
  // editor / runtime type at the call site (matches the existing pattern
  // for systemErrorCodes / purgeReasonCodes). When a Tier-1 catalog
  // earns a shared-types interface, swap the row type here.

  /** GET /v3/config/procedure-citations — Tier-1 paired read. */
  public procedureCitations(
    opts?: RequestOptions,
  ): Promise<ApiResult<ReadonlyArray<Record<string, unknown>>>> {
    return this.http.get<ApiResult<ReadonlyArray<Record<string, unknown>>>>(
      '/v3/config/procedure-citations',
      { ...opts },
    );
  }

  /** GET /v3/config/gate-keys — Tier-1 paired read. */
  public gateKeys(
    opts?: RequestOptions,
  ): Promise<ApiResult<ReadonlyArray<Record<string, unknown>>>> {
    return this.http.get<ApiResult<ReadonlyArray<Record<string, unknown>>>>(
      '/v3/config/gate-keys',
      { ...opts },
    );
  }

  /**
   * GET /v3/config/entity-rule-gate-enforcements — Tier-1 paired read.
   * Hot path: the cart-pipeline runtime resolves which gates apply per
   * (subtype × CIF-role) tuple from this list every account-open flow.
   */
  public entityRuleGateEnforcements(
    opts?: RequestOptions,
  ): Promise<ApiResult<ReadonlyArray<Record<string, unknown>>>> {
    return this.http.get<ApiResult<ReadonlyArray<Record<string, unknown>>>>(
      '/v3/config/entity-rule-gate-enforcements',
      { ...opts },
    );
  }

  /** GET /v3/config/deferred-gate-tracking — Tier-1 paired read. */
  public deferredGateTracking(
    opts?: RequestOptions,
  ): Promise<ApiResult<ReadonlyArray<Record<string, unknown>>>> {
    return this.http.get<ApiResult<ReadonlyArray<Record<string, unknown>>>>(
      '/v3/config/deferred-gate-tracking',
      { ...opts },
    );
  }

  /** GET /v3/config/product-customer-types — product × customer-type allow-list. */
  public productCustomerTypes(
    opts?: RequestOptions,
  ): Promise<ApiResult<ReadonlyArray<Record<string, unknown>>>> {
    return this.http.get<ApiResult<ReadonlyArray<Record<string, unknown>>>>(
      '/v3/config/product-customer-types',
      { ...opts },
    );
  }

  /** GET /v3/config/product-service-availability — Tier-1 paired read. */
  public productServiceAvailability(
    opts?: RequestOptions,
  ): Promise<ApiResult<ReadonlyArray<Record<string, unknown>>>> {
    return this.http.get<ApiResult<ReadonlyArray<Record<string, unknown>>>>(
      '/v3/config/product-service-availability',
      { ...opts },
    );
  }

  /** GET /v3/config/product-funding-method-availability — Tier-1 paired read. */
  public productFundingMethodAvailability(
    opts?: RequestOptions,
  ): Promise<ApiResult<ReadonlyArray<Record<string, unknown>>>> {
    return this.http.get<ApiResult<ReadonlyArray<Record<string, unknown>>>>(
      '/v3/config/product-funding-method-availability',
      { ...opts },
    );
  }

  /** GET /v3/config/product-summary-chip-config — Tier-1 paired read. */
  public productSummaryChipConfig(
    opts?: RequestOptions,
  ): Promise<ApiResult<ReadonlyArray<Record<string, unknown>>>> {
    return this.http.get<ApiResult<ReadonlyArray<Record<string, unknown>>>>(
      '/v3/config/product-summary-chip-config',
      { ...opts },
    );
  }

  /** GET /v3/config/wet-sign-attestation-box — Tier-1 paired read. */
  public wetSignAttestationBox(
    opts?: RequestOptions,
  ): Promise<ApiResult<ReadonlyArray<Record<string, unknown>>>> {
    return this.http.get<ApiResult<ReadonlyArray<Record<string, unknown>>>>(
      '/v3/config/wet-sign-attestation-box',
      { ...opts },
    );
  }

  /** GET /v3/config/disclosure-template-lifecycle — Tier-1 paired read. */
  public disclosureTemplateLifecycle(
    opts?: RequestOptions,
  ): Promise<ApiResult<ReadonlyArray<Record<string, unknown>>>> {
    return this.http.get<ApiResult<ReadonlyArray<Record<string, unknown>>>>(
      '/v3/config/disclosure-template-lifecycle',
      { ...opts },
    );
  }

  /** GET /v3/config/review-aggregate-chip — Tier-1 paired read. */
  public reviewAggregateChip(
    opts?: RequestOptions,
  ): Promise<ApiResult<ReadonlyArray<Record<string, unknown>>>> {
    return this.http.get<ApiResult<ReadonlyArray<Record<string, unknown>>>>(
      '/v3/config/review-aggregate-chip',
      { ...opts },
    );
  }

  /** GET /v3/config/home-dashboard-card — Tier-1 paired read. */
  public homeDashboardCard(
    opts?: RequestOptions,
  ): Promise<ApiResult<ReadonlyArray<Record<string, unknown>>>> {
    return this.http.get<ApiResult<ReadonlyArray<Record<string, unknown>>>>(
      '/v3/config/home-dashboard-card',
      { ...opts },
    );
  }

  /**
   * GET /v3/config/roles — the full roles catalog including built-in
   * (orthogonal) roles. Admin Panel Identity module uses this; the
   * editor decides whether each row is editable based on `isSystem`.
   */
  public roles(
    opts?: RequestOptions,
  ): Promise<ApiResult<ReadonlyArray<Record<string, unknown>>>> {
    return this.http.get<ApiResult<ReadonlyArray<Record<string, unknown>>>>(
      '/v3/config/roles',
      { ...opts },
    );
  }

  /** GET /v3/config/notification-event-bindings — Tier-1 paired read. */
  public notificationEventBindings(
    opts?: RequestOptions,
  ): Promise<ApiResult<ReadonlyArray<Record<string, unknown>>>> {
    return this.http.get<ApiResult<ReadonlyArray<Record<string, unknown>>>>(
      '/v3/config/notification-event-bindings',
      { ...opts },
    );
  }

  /**
   * GET /v3/config/notification-opt-outs — Tier-1 paired read.
   * Supports the same `?address=` / `?includeRestored=` / `?top=` query
   * params as the admin GET so dispatchers can resolve "is this recipient
   * opted out?" in one round-trip.
   */
  public notificationOptOuts(
    params?: { address?: string; includeRestored?: boolean; top?: number },
    opts?: RequestOptions,
  ): Promise<ApiResult<ReadonlyArray<Record<string, unknown>>>> {
    const qs: string[] = [];
    if (params?.address) qs.push(`address=${encodeURIComponent(params.address)}`);
    if (params?.includeRestored) qs.push('includeRestored=true');
    if (typeof params?.top === 'number') qs.push(`top=${params.top}`);
    const path = qs.length > 0
      ? `/v3/config/notification-opt-outs?${qs.join('&')}`
      : '/v3/config/notification-opt-outs';
    return this.http.get<ApiResult<ReadonlyArray<Record<string, unknown>>>>(
      path,
      { ...opts },
    );
  }

  /**
   * GET /v3/config/core-director-codes — Jack Henry Core Director code
   * mirror. Composite PK (domain, code). Used by the Admin Reference →
   * Codes editor and by RAM Wave 11 dialogs (ChangeOwnership, ChangeRateIndex).
   * Filter by `row.domain === 'CallClass'` / `'RateIndex'` etc. client-side.
   */
  public coreDirectorCodes(
    opts?: RequestOptions,
  ): Promise<ApiResult<ReadonlyArray<CoreDirectorCode>>> {
    return this.http.get<ApiResult<ReadonlyArray<CoreDirectorCode>>>(
      '/v3/config/core-director-codes',
      { ...opts },
    );
  }

  /**
   * GET /v3/config/alien-config — NRA Alien_CType catalog bundle (locked
   * 2026-04-27). Returns the six AlienConfig admin tables in one envelope:
   * W-8 types, IRS reporting types, recipient codes, exemption codes,
   * treaty countries, eligible visa types. Backs IndividualNraSection.
   */
  public alienConfig(
    opts?: RequestOptions,
  ): Promise<ApiResult<AlienConfigEnvelope>> {
    return this.http.get<ApiResult<AlienConfigEnvelope>>(
      '/v3/config/alien-config',
      { ...opts },
    );
  }

  /**
   * GET /v3/config/wizard-doc-matrix — cohort → tile catalog for the
   * new-person wizard. Returned shape is `{ cohorts: Record<cohort, tiles[]> }`.
   * See packages/api-client/src/domains/admin-wizard-doc-matrix.ts for
   * the matching admin write surface.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public wizardDocMatrix(opts?: RequestOptions): Promise<any> {
    return this.http.get('/v3/config/wizard-doc-matrix', { ...opts });
  }

  /**
   * GET /v3/config/compliance-rule-applications — rules that map a trigger
   * event to a compliance gate's enforcement decision. Backs the trigger-time
   * rules engine + ComplianceRuleApplicationsEditor.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public complianceRuleApplications(opts?: RequestOptions): Promise<any> {
    return this.http.get('/v3/config/compliance-rule-applications', { ...opts });
  }

  /**
   * GET /v3/config/compliance-gates — catalog of compliance gate definitions
   * (gate code, label, description, severity). Backs the rules engine.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public complianceGates(opts?: RequestOptions): Promise<any> {
    return this.http.get('/v3/config/compliance-gates', { ...opts });
  }

  /**
   * GET /v3/config/ln-verification-rules — BACKLOG-0501 admin-toggleable
   * matrix that drives the Verify-tab LN gate (PROHIBIT / OPTIONAL / REQUIRED).
   * Returns active rows only; admin GET on /v3/admin/ln-verification-rules
   * exposes the inactive ones for the matrix editor. PersonModal + the
   * cart-commit preflight both read this.
   */
  public lnVerificationRules(
    opts?: RequestOptions,
  ): Promise<ApiResult<ReadonlyArray<LnVerificationRule>>> {
    return this.http.get<ApiResult<ReadonlyArray<LnVerificationRule>>>(
      '/v3/config/ln-verification-rules',
      { ...opts },
    );
  }
}
